import { criterion, fromCriteria, unsupported } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readNotes } from "@/office/word/references";

export const lektionFussnoten: Lesson = {
  id: "fussnoten",
  title: "Fuß- und Endnoten",
  summary: "Anmerkungen unterbringen, ohne den Lesefluss zu stören.",
  durationMinutes: 8,
  learningGoals: [
    "Fußnoten einfügen und verwalten",
    "Fußnoten von Endnoten unterscheiden",
    "Die automatische Nummerierung nutzen",
  ],
  steps: [
    {
      id: "fussnote-einfuegen",
      title: "Eine Fußnote einfügen",
      explanation: [
        "Eine Fußnote nimmt auf, was zum Verständnis beiträgt, den Satz aber sprengen " +
          "würde: eine Quellenangabe, eine Begriffserklärung, ein Seitenverweis.",
        "Der Weg ist kurz: Cursor an die Stelle im Text, dann „Verweise“ → „Fußnote " +
          "einfügen“ oder Strg + Alt + F. Word setzt eine hochgestellte Zahl in den Text " +
          "und springt an den Seitenfuß, wo du den Text eintippst.",
        "Die Nummerierung übernimmt Word – und pflegt sie auch. Fügst du später eine " +
          "Fußnote dazwischen ein, zählt es alles neu. Löschst du die hochgestellte Zahl " +
          "im Text, verschwindet die zugehörige Fußnote automatisch mit.",
        "Endnoten funktionieren genauso, sammeln sich aber am Dokumentende statt am " +
          "Seitenfuß. Sie werden vor allem in Geisteswissenschaften und bei Büchern " +
          "verwendet. Der Befehl dafür ist Strg + Alt + D.",
      ],
      keyPoints: [
        "Strg + Alt + F für eine Fußnote, Strg + Alt + D für eine Endnote.",
        "Fußnote: am Seitenfuß. Endnote: am Dokumentende.",
        "Die Nummerierung pflegt Word selbst.",
      ],
      task: {
        instruction: "Füge deinem Dokument mindestens zwei Fußnoten mit Text hinzu.",
        hint: "Cursor hinter ein Wort setzen, Strg + Alt + F drücken, Text eintippen.",
        check: async () => {
          const notizen = await readNotes();
          if (notizen === null) {
            return unsupported(
              "Fuß- und Endnoten sind dem Add-in erst ab WordApi 1.5 zugänglich – diese " +
                "Word-Version stellt sie nicht zur Verfügung.",
              "Prüfe selbst, ob am Seitenfuß zwei nummerierte Anmerkungen stehen, und markiere den Schritt dann als erledigt.",
            );
          }

          const mitText = notizen.footnotes.filter((notiz) => notiz.text.length > 0);
          return fromCriteria(
            [
              criterion(
                `Mindestens zwei Fußnoten (gefunden: ${notizen.footnotes.length})`,
                notizen.footnotes.length >= 2,
              ),
              criterion(
                `Alle Fußnoten enthalten Text (gefüllt: ${mitText.length})`,
                notizen.footnotes.length > 0 && mitText.length === notizen.footnotes.length,
              ),
            ],
            {
              success: "Die Fußnoten sitzen – und nummerieren sich ab jetzt selbst.",
              failure: "Da fehlt noch etwas.",
              hint: "Eine hochgestellte Zahl von Hand zu tippen erzeugt keine Fußnote. Nutze Verweise → Fußnote einfügen.",
            },
          );
        },
      },
    },
    {
      id: "verwalten",
      title: "Fußnoten verwalten",
      explanation: [
        "Über „Verweise“ → den kleinen Pfeil in der Gruppe „Fußnoten“ öffnest du die " +
          "Einstellungen. Dort lässt sich festlegen, wo die Noten stehen, wie sie " +
          "nummeriert werden (fortlaufend, pro Seite neu, pro Abschnitt neu) und welches " +
          "Zahlenformat verwendet wird.",
        "Praktisch ist auch „Nächste Fußnote“ in derselben Gruppe: Damit springst du von " +
          "Note zu Note, ohne zu scrollen. Und ein Doppelklick auf die Zahl am Seitenfuß " +
          "bringt dich zurück an die Stelle im Text.",
        "Ein Hinweis für längere Arbeiten: Der Text einer Fußnote steht in der " +
          "Formatvorlage „Fußnotentext“. Willst du alle Fußnoten kleiner oder mit anderem " +
          "Abstand setzen, änderst du diese Vorlage – wie im Formatvorlagen-Modul gelernt, " +
          "und nicht jede Fußnote einzeln.",
      ],
      keyPoints: [
        "Verweise → kleiner Pfeil bei „Fußnoten“ öffnet die Einstellungen.",
        "Doppelklick auf die Zahl springt zwischen Text und Fußnote.",
        "Das Aussehen steuert die Formatvorlage „Fußnotentext“.",
      ],
    },
  ],
};
